import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSEO } from "@/hooks/useSEO";
import { BRAND_HANDLE, BRAND_PERSON_NAME, BRAND_SITE_URL } from "@/lib/brandConfig";
import { BaseTemplate } from "@/components/templates/BaseTemplate";

/**
 * Colombia — Public SEO landing page using BaseTemplate (rich layout + admin control).
 * Supports /colombia (ES) and /en/colombia (EN) for English positioning.
 * Uses BaseTemplate for hero (mux video + Stripe CTA for intl), gallery, contact etc.
 * Admin control via site-settings, heroCta, country-template-config (CO uses Stripe).
 * Matches Argentina BaseTemplate capability (per "region" via country + city entry points).
 */
const Colombia = () => {
  const location = useLocation();
  const { i18n } = useTranslation();
  const isEn = location.pathname.startsWith("/en/") || location.pathname.startsWith("/en");

  // Force English for /en/ alternative routes (SEO + language consistency)
  useEffect(() => {
    if (isEn && i18n.language?.split("-")[0] !== "en") {
      void i18n.changeLanguage("en");
    }
  }, [isEn, i18n]);

  const locale = isEn ? "en_US" : "es_CO";
  const title = isEn
    ? `${BRAND_PERSON_NAME} | Content Creator in Colombia — Lifestyle, Fitness & Beauty`
    : `${BRAND_PERSON_NAME} | Creadora de Contenido en Colombia — Lifestyle, Fitness y Belleza`;
  const description = isEn
    ? `Exclusive content from ${BRAND_PERSON_NAME} for Colombia. Premium lifestyle, fitness and beauty videos & photos. Join the private community with Stripe checkout.`
    : `Contenido exclusivo de ${BRAND_PERSON_NAME} para Colombia. Videos y fotos premium de lifestyle, fitness y belleza. Únete a la comunidad privada con pago vía Stripe.`;
  const keywords = isEn
    ? `${BRAND_PERSON_NAME}, colombia, content creator colombia, lifestyle colombia, fitness colombia, belleza colombia, exclusive content, premium membership, delfuocampo colombia`
    : `${BRAND_PERSON_NAME}, colombia, creadora contenido colombia, lifestyle colombia, fitness colombia, belleza colombia, contenido exclusivo, membresia premium, delfuocampo colombia`;

  useSEO({
    title,
    description,
    keywords,
    ogImage: `${BRAND_SITE_URL}/og.png`,
    ogTitle: title,
    ogDescription: description,
    canonical: `${BRAND_SITE_URL}${location.pathname.replace(/\/$/, "") || "/colombia"}`,
    author: BRAND_PERSON_NAME,
    locale,
    alternateLocale: isEn ? "es_CO" : "en_US",
    type: "website",
    publishedTime: "2025-01-01T00:00:00.000Z",
    modifiedTime: new Date().toISOString(),
  });

  const heroTitle = isEn
    ? `${BRAND_PERSON_NAME} in Colombia`
    : `${BRAND_PERSON_NAME} Colombia`;
  const heroDescription = isEn
    ? "Exclusive lifestyle, fitness & beauty content for my Colombian community. Private access with Stripe."
    : "Contenido exclusivo de lifestyle, fitness y belleza para mi comunidad en Colombia. Acceso privado con Stripe.";

  return (
    <BaseTemplate
      heroTitle={heroTitle}
      heroDescription={heroDescription}
      showSobreMi={false}
      showPortfolioNavLink
      showGalleryNavLink
      showContactNavLink
      showGallerySection
      showTallySection
    />
  );
};

export default Colombia;
